import { TeamMemberUpgradeDialog } from '@/components/shared/TeamMemberUpgradeDialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTeamInvites } from '@/hooks/useTeamInvites';
import { TeamRole, useTeamMembers } from '@/hooks/useTeamMembers';
import { useUser } from '@/hooks/useUser';
import { hasAdminPermissions, isCurrentUser } from '@/utils/permissions';
import { yupResolver } from '@hookform/resolvers/yup';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as yup from 'yup';

const inviteFormSchema = yup.object({
  name: yup.string().required('Name is required'),
  email: yup.string().email('Please enter a valid email address').required(),
  role: yup.string().oneOf(['ADMIN', 'MEMBER']).required(),
});

type InviteFormValues = yup.InferType<typeof inviteFormSchema>;

export const TeamMembersPage = () => {
  const {
    data: team,
    isLoading: isMembersLoading,
    updateTeamMember,
    deleteTeamMember,
  } = useTeamMembers();
  const { inviteTeamMember, deleteInvite } = useTeamInvites();
  const { data: user } = useUser();
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  const currentUserRole = team?.users?.find(
    (member) => member.user_id === user?.id,
  )?.role;

  const isAdmin = hasAdminPermissions(currentUserRole);
  const totalMembers =
    (team?.users?.length || 0) + (team?.invites?.length || 0);
  const maxFreeMembers = 2;
  const canAddMembers = totalMembers < maxFreeMembers;

  const form = useForm<InviteFormValues>({
    resolver: yupResolver(inviteFormSchema),
    defaultValues: {
      name: '',
      email: '',
      role: 'MEMBER',
    },
    mode: 'onSubmit',
  });

  const handleInviteTeamMember = async (values: InviteFormValues) => {
    if (!canAddMembers) {
      setShowUpgradeDialog(true);

      return;
    }

    try {
      await inviteTeamMember.mutateAsync({
        name: values.name,
        email: values.email,
        role: values.role,
      });

      toast.success('Team member added successfully!');
      form.reset();
    } catch (error) {
      console.error('Failed to invite member:', error);
      toast.error('Failed to add team member. Please try again.');
    }
  };

  const handleRoleChange = async (memberId: string, newRole: string) => {
    try {
      await updateTeamMember.mutateAsync({
        userId: memberId,
        role: newRole.toUpperCase() as TeamRole,
      });
    } catch (error) {
      console.error('Failed to update member role:', error);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      await deleteTeamMember.mutateAsync(memberId);
    } catch (error) {
      console.error('Failed to remove member:', error);
    }
  };

  const handleRemoveInvite = async (inviteId: string) => {
    try {
      await deleteInvite.mutateAsync(inviteId);
    } catch (error) {
      console.error('Failed to remove invite:', error);
    }
  };

  const isFormValid = form.formState.isValid;

  if (isMembersLoading) {
    return <SkeletonLoader />;
  }

  return (
    <Card>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleInviteTeamMember)}>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[200px]">Name</TableHead>
                <TableHead className="w-[300px]">Email</TableHead>
                <TableHead className="w-[140px]">Role</TableHead>
                <TableHead className="w-[120px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {team?.users?.map((member) => (
                <TableRow key={member.user_id} className="hover:bg-transparent">
                  <TableCell className="w-[200px]">
                    {member.user?.name}
                  </TableCell>
                  <TableCell className="w-[300px]">
                    {member.user?.email}
                  </TableCell>
                  <TableCell className="w-[140px]">
                    {member.role === 'OWNER' ||
                    isCurrentUser(member.user_id, user?.id) ? (
                      <span className="text-muted-foreground capitalize">
                        {member.role.toLowerCase()}
                      </span>
                    ) : (
                      isAdmin && (
                        <Select
                          defaultValue={member.role.toLowerCase()}
                          onValueChange={(value) =>
                            handleRoleChange(member.user_id, value)
                          }>
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="member">Member</SelectItem>
                          </SelectContent>
                        </Select>
                      )
                    )}
                  </TableCell>
                  <TableCell className="text-right w-[120px]">
                    {member.role !== 'OWNER' &&
                      !isCurrentUser(member.user_id, user?.id) &&
                      isAdmin && (
                        <Button
                          isLoading={deleteTeamMember.isPending}
                          onClick={(e) => {
                            e.preventDefault();
                            handleRemoveMember(member.user_id);
                          }}
                          variant="destructive"
                          size="sm"
                          icon={<Trash2 className="h-4 w-4" />}
                          className="whitespace-nowrap">
                          Remove
                        </Button>
                      )}
                  </TableCell>
                </TableRow>
              ))}
              {isAdmin &&
                team?.invites?.map((invite) => (
                  <TableRow key={invite.id} className="hover:bg-transparent">
                    <TableCell>
                      <span className="text-muted-foreground">
                        {invite.name}
                      </span>
                    </TableCell>
                    <TableCell>{invite.email}</TableCell>
                    <TableCell>
                      <span className="text-muted-foreground capitalize">
                        {invite.role.toLowerCase()}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {isAdmin && (
                        <Button
                          isLoading={
                            deleteInvite.isPending &&
                            deleteInvite.variables === invite.id
                          }
                          disabled={deleteInvite.isPending}
                          onClick={(e) => {
                            e.preventDefault();
                            handleRemoveInvite(invite.id);
                          }}
                          variant="ghost"
                          size="sm"
                          icon={<Trash2 className="h-4 w-4" />}>
                          Cancel
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              {isAdmin && (
                <TableRow className="hover:bg-transparent">
                  {canAddMembers ? (
                    <>
                      <TableCell>
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input placeholder="John Doe" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  placeholder="member@example.com"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <FormField
                          control={form.control}
                          name="role"
                          render={({ field }) => (
                            <FormItem>
                              <Select
                                value={field.value}
                                onValueChange={field.onChange}>
                                <FormControl>
                                  <SelectTrigger className="w-[140px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="ADMIN">Admin</SelectItem>
                                  <SelectItem value="MEMBER">Member</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          isLoading={inviteTeamMember.isPending}
                          disabled={!isFormValid}
                          type="submit"
                          size="sm">
                          Invite
                        </Button>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell colSpan={3} className="text-muted-foreground">
                        You've reached the maximum number of free team members.
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          onClick={() => setShowUpgradeDialog(true)}
                          size="sm">
                          Upgrade Now
                        </Button>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </form>
      </Form>

      <TeamMemberUpgradeDialog
        isOpen={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        currentMemberCount={totalMembers}
      />
    </Card>
  );
};

const SkeletonLoader = () => (
  <Card>
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Role</TableHead>
          <TableHead className="w-[120px]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {[1, 2, 3].map((index) => (
          <TableRow key={index} className="hover:bg-transparent">
            <TableCell>
              <Skeleton className="h-4 w-[120px]" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-[180px]" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-9 w-[140px]" />
            </TableCell>
            <TableCell className="text-right">
              <Skeleton className="h-9 w-[100px] ml-auto" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Card>
);
